'use no memo';
import { Contract } from '@kalos/kalos-rpc';
import {
  Button,
  DataTable,
  DataTableColumnHeader,
  Form,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import { MagnifyingGlassIcon, PersonIcon } from '@radix-ui/react-icons';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME } from '../../../../constants';
import {
  type ContractFilter,
  useContractsBatchGetQuery,
} from '../../../../hooks/react-query/useContractsQuery';
import { useMobileDevice } from '../../../../hooks/useMedia';
import {
  type ContractsSort,
  formatDate,
  generateDateRangeAndDateTargetFromDates,
} from '../../../../tools/helpers';
import { ExportJSON, type Status } from '../../ExportJSON';
import { downloadJsonAsCsv } from '../../ExportJSON/downloadJsonAsCsv';
import { filtersKey } from '../listState';
import { useAdvancedSearchListState } from '../paginationStore';
import ContractsSearchForm from './ContractsSearchForm';
import { ContractsSearchPhone } from './ContractsSearchPhone';
import { type ContractsSearchScheme, useContractsSearchForm } from './utils';

const loadingContracts: Contract[] = Array.from({ length: 5 }, (_) => Contract.create());

export const ContractsSearch = () => {
  const { page, setPage, setTotalEntriesCount, sort, setSort } = useAdvancedSearchListState();

  const contractSearchForm = useContractsSearchForm();

  const [contractsFilter, setContractsFilter] = useState<ContractsSearchScheme>(
    contractSearchForm.getValues(),
  );

  // The page resets only when the filters really change, not when the form first loads from
  // the URL, so a reload keeps the page.
  const appliedFiltersKey = useRef(filtersKey(contractsFilter));
  useEffect(() => {
    const debouncedUpdate = debounce((data: Partial<ContractsSearchScheme>) => {
      const nextKey = filtersKey(data);
      if (nextKey === appliedFiltersKey.current) return;
      appliedFiltersKey.current = nextKey;
      setPage(0);
      setContractsFilter((prev) => ({ ...prev, ...data }));
    }, FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME);
    const updateSubscription = contractSearchForm.watch(debouncedUpdate);
    return () => updateSubscription.unsubscribe();
  }, [setPage, contractSearchForm]);

  // Table column ids are camelCase; the request's order-by names are not.
  const contractsSort = useMemo<ContractsSort>(() => {
    if (!sort) return { orderByField: 'number', orderBy: 'contract_number', orderDir: 'ASC' };
    const orderByName: Record<string, string> = {
      dateStarted: 'date_Started',
      dateEnded: 'date_Ended',
      businessName: 'business_name',
      lastName: 'last_name',
    };
    return {
      orderByField: sort.id as keyof Contract,
      orderDir: sort.desc ? 'DESC' : 'ASC',
      orderBy: `contract_${orderByName[sort.id] ?? sort.id}`,
    };
  }, [sort]);

  const sorting = useMemo<SortingState>(() => (sort ? [sort] : []), [sort]);

  const onSortChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const newState = typeof updater === 'function' ? updater(sorting) : updater;
      setSort(newState[0] ? { id: newState[0].id, desc: newState[0].desc } : null);
    },
    [setSort, sorting],
  );

  const contractsFilterCriteria = useMemo<ContractFilter>(() => {
    const { dateRange, dateTarget } = generateDateRangeAndDateTargetFromDates(
      contractsFilter.dateStarted,
      contractsFilter.dateEnded,
      {
        startDateFieldName: 'contract_date_Started',
        endDateFieldName: 'contract_date_Ended',
      },
    );

    return {
      isActive: 1,
      businessName: contractsFilter.businessName ? `%${contractsFilter.businessName}%` : undefined,
      lastName: contractsFilter.lastName ? `%${contractsFilter.lastName}%` : undefined,
      number: contractsFilter.number ? `%${contractsFilter.number}%` : undefined,
      notes: contractsFilter.notes ? `%${contractsFilter.notes}%` : undefined,
      paymentTerms: contractsFilter.paymentTerms ? `%${contractsFilter.paymentTerms}%` : undefined,
      paymentStatus: contractsFilter.paymentStatus ? contractsFilter.paymentStatus : undefined,

      pageNumber: page,

      dateRange,
      dateTarget,

      orderBy: contractsSort.orderBy,
      orderDir: contractsSort.orderDir,
    };
  }, [
    contractsFilter.businessName,
    contractsFilter.dateEnded,
    contractsFilter.dateStarted,
    contractsFilter.lastName,
    contractsFilter.notes,
    contractsFilter.number,
    contractsFilter.paymentStatus,
    contractsFilter.paymentTerms,
    contractsSort.orderBy,
    contractsSort.orderDir,
    page,
  ]);

  const [isJsonDataQueryEnabled, setIsJsonDataQueryEnabled] = useState<boolean>(false);

  const isMobileDevice = useMobileDevice();

  // Phones load page after page as the list scrolls (ContractsSearchPhone), so the paged query
  // is the desktop table's only.
  const contractsQuery = useContractsBatchGetQuery({
    filter: contractsFilterCriteria,
    enabled: !isMobileDevice,
  });

  const jsonExportDataQuery = useContractsBatchGetQuery({
    filter: useMemo(
      () => ({ ...contractsFilterCriteria, withoutLimit: true, pageNumber: 0 }),
      [contractsFilterCriteria],
    ),
    enabled: isJsonDataQueryEnabled,
    select(data) {
      return data.results.map((contract) => ({
        'Contract #': contract.number,
        'Contract Start Date': formatDate(String(contract.dateStarted)),
        'Expiration Date': formatDate(String(contract.dateEnded)),
        'Contract Business Name': contract.businessName,
        'Contract Last Name': contract.lastName,
      }));
    },
  });

  useEffect(() => {
    if (contractsQuery.isSuccess) {
      setTotalEntriesCount(contractsQuery.data?.totalCount || 0);
    }
  }, [contractsQuery.data?.totalCount, contractsQuery.isSuccess, setTotalEntriesCount]);

  const contractTableColumnsDefs = useMemo<ColumnDef<Contract>[]>(() => {
    const columns: ColumnDef<Contract>[] = [
      {
        accessorKey: 'number',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Contract #" column={column} />
        ),
        cell({ row }) {
          const value = row.getValue('number');
          return <div className="w-32">{String(value)}</div>;
        },
        sortingFn: () => 0,
      },
      {
        accessorKey: 'dateStarted',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Contract Start Date" column={column} />
        ),
        cell({ row }) {
          const value = row.getValue('dateStarted');
          return <div className="w-32">{formatDate(String(value))}</div>;
        },
        sortingFn: () => 0,
      },
      {
        accessorKey: 'dateEnded',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Expiration Date" column={column} />
        ),
        cell({ row }) {
          const value = row.getValue('dateEnded');
          return <div className="w-32">{formatDate(String(value))}</div>;
        },
        sortingFn: () => 0,
      },
      {
        accessorKey: 'businessName',
        header: ({ column }) => (
          <DataTableColumnHeader
            hideVisibilityToggle
            title="Contract Business Name"
            column={column}
          />
        ),
        cell({ row }) {
          const phone = row.getValue('businessName');
          return <div className="w-32">{String(phone)}</div>;
        },
        sortingFn: () => 0,
      },
      {
        accessorKey: 'lastName',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Contract Last Name" column={column} />
        ),
        cell({ row }) {
          const email = row.getValue('lastName');
          return <div className="w-32 break-all">{String(email)}</div>;
        },
        sortingFn: () => 0,
      },
      {
        id: 'actions',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} hideVisibilityToggle title="Actions" />
        ),
        cell({ row }) {
          // SHould it be possible?
          if (!row.original.userId) return null;

          const customerUrl = `/customers/${row.original.userId}`;
          return (
            <div className="flex items-center space-x-2">
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      asChild
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      variant="outline"
                    >
                      <Link to={customerUrl}>
                        <MagnifyingGlassIcon />
                      </Link>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="px-2 py-1">View Contract</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      asChild
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Link to={customerUrl}>
                        <PersonIcon />
                      </Link>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="px-2 py-1">View Customer</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        },
      },
    ];
    return columns;
  }, []);

  const skeletonColumns = useMemo<ColumnDef<Contract>[]>(() => {
    return contractTableColumnsDefs.map((column) => ({
      ...column,
      cell: () => <Skeleton className="bg-foreground/30 h-4 w-32" />,
    }));
  }, [contractTableColumnsDefs]);

  const table = useReactTable({
    data: contractsQuery.isPending ? loadingContracts : contractsQuery.data?.results ?? [],
    columns: contractsQuery.isPending ? skeletonColumns : contractTableColumnsDefs,
    state: {
      sorting,
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
    },
    onSortingChange: onSortChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });
  const navigate = useNavigate();

  const onRowClick = useCallback(
    (entry: Contract) => {
      if (!entry.userId) return;
      // TODO fix
      navigate({ to: '/customers/$customerId', params: { customerId: entry.userId } });
    },
    [navigate],
  );

  const handleExport = useCallback(async () => {
    setIsJsonDataQueryEnabled(true);
  }, []);
  const handleExported = useCallback(() => setIsJsonDataQueryEnabled(false), []);

  const exportJSonStatus: Status = isJsonDataQueryEnabled
    ? jsonExportDataQuery.isSuccess
      ? 'loaded'
      : 'loading'
    : 'idle';

  // On phones the export runs from a menu item, which can't keep the export button mounted to
  // download the file, so the download happens here once the rows arrive. Same file, same headers.
  useEffect(() => {
    if (!isMobileDevice || exportJSonStatus !== 'loaded') return;
    downloadJsonAsCsv(
      `Contract_report_${format(new Date(), 'dd_MMM_yyyy')}`,
      jsonExportDataQuery.data ?? [],
    );
    handleExported();
  }, [exportJSonStatus, handleExported, isMobileDevice, jsonExportDataQuery.data]);

  if (isMobileDevice) {
    return (
      <ContractsSearchPhone
        form={contractSearchForm}
        request={contractsFilterCriteria}
        sort={sort}
        onSortChange={setSort}
        onExport={() => void handleExport()}
        isExporting={exportJSonStatus === 'loading'}
      />
    );
  }

  return (
    <>
      <div className="space-y-2 p-4">
        <Form {...contractSearchForm}>
          <ContractsSearchForm />
        </Form>

        <ExportJSON
          filename={() => `Contract_report_${format(new Date(), 'dd_MMM_yyyy')}`}
          status={exportJSonStatus}
          onExport={handleExport}
          onExported={handleExported}
          json={jsonExportDataQuery.data ?? []}
        />
      </div>

      <div className="max-w-full overflow-x-auto">
        <DataTable table={table} onRowClick={onRowClick} />
      </div>
    </>
  );
};
